import { CATALOG_ITEM_KINDS, CATALOG_RESOURCES, catalogResource } from "@authe/fe-commons/catalog";
import type { ResourceReadPolicy } from "@authe/fe-commons/events";

/** One inventory for query invalidation and CI coverage. New read families must be classified. */
export interface QueryResourcePolicy {
  policy: ResourceReadPolicy | ((key: readonly unknown[]) => ResourceReadPolicy);
  /** Other local mutation adapters reuse these groups, never their own key lists. */
  group?: "address";
}
const catalog: QueryResourcePolicy = { policy: { resources: CATALOG_RESOURCES } };
const address: QueryResourcePolicy = { ...catalog, group: "address" };
const byKind: QueryResourcePolicy = { policy: (key) => ({ resources:
  CATALOG_ITEM_KINDS.includes(key[1] as typeof CATALOG_ITEM_KINDS[number]) ? [catalogResource(key[1] as typeof CATALOG_ITEM_KINDS[number])] : [] }) };
const independent = (reason: string): QueryResourcePolicy => ({ policy: { independent: reason } });

export const queryResourcePolicies: Readonly<Record<string, QueryResourcePolicy>> = {
  "catalog-item": byKind,
  "catalog-rows": byKind,
  "catalog-names": byKind,
  "catalog-chains": byKind,
  "catalog-field-overrides": byKind,
  "catalog-related": catalog,
  "catalog-attached-entities": catalog,
  "catalog-attachment-count": catalog,
  "catalog-addresses-attached": catalog,
  "catalog-addresses-headline": catalog,
  "entity-memberships": catalog,
  "entity-pool-probe": catalog,
  "tx-catalog-batch": catalog,
  // GraphQL resolves attribution server-side, so refresh these rows on catalog changes too.
  "recent-tx-feed": catalog,
  "tx-exact-lookup": catalog,
  "tx-tenant-triage": catalog,
  "graph-assign-entities": catalog,
  // One entry per (chain, address); the batch is a transport detail, not a
  // cache identity — see `features/overlays/addressOverlayRefCache`.
  "address-overlay-ref": address,
  "address-overlays": address,
  "address-overlays-rest": address,
  "address-cross-chain": address,
  "address-detail": address,
  "address-row": address,
  "addresses-list": address,
  "tx-addr-row": address,
  "address-assignments": address,
  "tx-addr-assignments": address,
  "entity-mapping": address,
  "address-primary-chains": address,
  "address-entity-meta": address,
  // The pricing/poisoning evidence read on an address page: stored transactions
  // and their bodies, judged by the pipeline's own decode. A catalog edit
  // (renaming an entity, attaching a tag) changes none of it.
  "address-stored-evidence": independent(
    "Stored transactions and their decoded bodies for one address, not catalog data.",
  ),
  "notification-scope": independent("Organization, tenant and member choices, not catalog data."),
  "subscriptions": independent("Billing subscription state."),
  "grouping": independent("Rule definitions and executable specs, not catalog groups."),
  "pipelines": independent("Pipeline definitions for reruns."),
  "tx-header": independent("Objective first-paint header and notes; catalog is read by transaction detail/batches."),
  "tx-filter-capability": independent("Platform capability of the list's index-backed filters (the persistence.filter_indexes pipeline setting), not catalog data."),
  "decode-gap-proxy-batch": independent("Contract implementation/code probes."),
  "decode-gap-abi": independent("Contract ABI probes."),
  "contract-is-code": independent("On-chain bytecode probe."),
  "pool-contract-composition": independent("On-chain pool getters (token0/token1/fee); immutable, not catalog data."),
  "token-owners": independent("Live provider holder balances; holder labels are resolved by the graph catalog read beside it."),
  "token-holder-stats": independent("Live provider count of every holder (total, top-N supply, change windows); not stored, not catalog data."),
  "onchain-feed": independent("Blockchain rows and topology; current catalog is resolved by overlay batches."),
  "org-roles": independent("Organization authorization roles."),
  "org-users": independent("Organization member directory."),
  "my-teams": independent("Team membership directory."),
  "team-memberships": independent("Identity-scoped team authorization; refreshes on focus and every minute independently of streaming."),
  "collaboration-roster": independent("Identity/team-scoped mention roster; refreshes on focus and every minute."),
  "address-cluster": independent("Heuristic cluster structure, not catalog attribution."),
  "actor-identities": independent("Related-addresses identity membership lookup; catalog attribution is read separately."),
  "address-cluster-evidence": independent("Heuristic cluster evidence."),
  "address-cluster-roles": independent("Heuristic role assignments."),
  "address-cluster-roles-for": independent("Heuristic role assignments."),
  "cluster-overrides": independent("Authored cluster overrides."),
  "seed-reviews": catalog,
  "usage": independent("Billing usage counters."),
  "feature-flags": independent("Operator-set customer feature flags, read once per tenancy."),
  "enforced-policy": independent("Operator-set visibility policy (admin portal only), read once per tenancy and refreshed every minute."),
  "team-roles": independent("Team authorization roles."),
  "team-capabilities": independent("Active team membership and team-role grants, not catalog data."),
};

export function queryDependsOnResources(key: readonly unknown[], resources: ReadonlySet<string>): boolean {
  const entry = queryResourcePolicies[String(key[0])];
  if (!entry) return false;
  const policy = typeof entry.policy === "function" ? entry.policy(key) : entry.policy;
  return policy.resources?.some((resource) => resources.has(resource)) ?? false;
}

export const ADDRESS_QUERY_PREFIXES: readonly (readonly [string])[] = Object.entries(queryResourcePolicies)
  .filter(([, entry]) => entry.group === "address").map(([key]) => [key] as const);
